import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import {
  Table,
  Button,
  Container,
  Form,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import { API_ENDPOINTS } from "./config/api";
import PageTitle from "./components/PageTitle";
import { getRatingOptions } from "./utils/ratingScales";

const CompletedEvaluationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/");
          return;
        }

        // Determine the correct API endpoint based on the URL path
        const isAttendingPath = window.location.pathname.includes('/attending/');
        const endpoint = isAttendingPath 
          ? `${API_ENDPOINTS.ATTENDING}/completed-evaluation/${id}`
          : `${API_ENDPOINTS.RESIDENT}/completed-evaluation/${id}`;

        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setEvaluation(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching evaluation:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEvaluation();
  }, [id, navigate]);

  const questionRefs = useRef({});
  const questions = evaluation?.template?.questions || [];
  const numberedQuestions = questions.filter(
    (q) => !["header-paragraph", "divider"].includes(q.questionType)
  );

  const isQuestionFilled = (question) => {
    const answer = evaluation.answers[question.id];
    if (question.questionType === "checkbox") {
      return Array.isArray(answer) && answer.length > 0;
    }
    if (question.questionType === "radio-matrix") {
      return answer && Object.keys(answer).length > 0;
    }
    return answer !== undefined && answer !== "" && answer !== null;
  };

  const answeredCount = numberedQuestions.filter(isQuestionFilled).length;
  const totalQuestions = numberedQuestions.length;
  const progressPercent =
    totalQuestions > 0
      ? Math.round((answeredCount / totalQuestions) * 100)
      : 0;

  const formattedDate = useMemo(() => {
    if (!evaluation?.createdAt) return "Date not available";
    return new Date(evaluation.createdAt).toLocaleString();
  }, [evaluation?.createdAt]);

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Evaluation Details" />
        <Container className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar pageTitle="Evaluation Details" />
        <Container className="py-4">
          <Alert variant="danger">Error: {error}</Alert>
        </Container>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div>
        <PageTitle title="Evaluation Not Found" />
        <Navbar pageTitle="Evaluation Details" />
        <Container className="py-4">
          <Alert variant="warning">Evaluation not found</Alert>
        </Container>
      </div>
    );
  }

  const renderAnswer = (question, answer) => {
    switch (question.questionType) {
      case "header-paragraph":
        try {
          const parsed = JSON.parse(question.questionText);
          return (
            <div>
              <div
                style={{
                  fontSize:
                    parsed.size === "h1"
                      ? "32px"
                      : parsed.size === "h2"
                      ? "24px"
                      : "20px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                  color: "var(--gray-800)",
                }}
              >
                {parsed.header}
              </div>
              <div
                style={{
                  marginBottom: "20px",
                  color: "var(--muted-text)",
                  lineHeight: "1.5",
                }}
              >
                {parsed.paragraph}
              </div>
            </div>
          );
        } catch (e) {
          return <p>{question.questionText}</p>;
        }

      case "text":
        return <div className="p-3 rounded-3 bg-light">{answer || "—"}</div>;

      case "textarea":
        return (
          <div className="p-3 rounded-3 bg-light">{answer || "—"}</div>
        );

      case "radio-matrix":
        return (
          <div className="table-responsive custom-modern-responsive">
          <Table>
            <thead>
              <tr>
                <th className="px-3">Performance Factor</th>
                {(question.columns && question.columns.length > 0
                  ? question.columns
                  : [
                      "Unsatisfactory",
                      "Needs Improvement",
                      "Meets Expectations",
                      "Exceeds Expectations",
                    ]
                ).map((col, colIdx) => (
                  <th key={colIdx}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {question.options.map((opt, idx) => (
                <tr key={idx}>
                  <td className="px-3">{opt}</td>
                  {(question.columns && question.columns.length > 0
                    ? question.columns
                    : [
                        "Unsatisfactory",
                        "Needs Improvement",
                        "Meets Expectations",
                        "Exceeds Expectations",
                      ]
                  ).map((_, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        boxShadow: answer && answer[idx] === colIdx ? 'inset 0 0 0 9999px var(--gray-300)': '',
                        borderRadius: answer && answer[idx] === colIdx ? '0px' : '',
                      
                      }}
                    >
                      <div
                        className="rounded-circle"
                        style={{
                          width: 16,
                          height: 16,
                          margin: "0 auto",
                          backgroundColor: answer && answer[idx] === colIdx ? 'var(--blue)' : '',
                          border: answer && answer[idx] === colIdx ? '1px solid var(--blue)' : ' 1px solid var(--gray-400)',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '3px auto',
                            width: '8px',
                            borderRadius: '50%',
                            height: '8px',
                            backgroundColor: answer && answer[idx] === colIdx ? 'var(--white)' : '',
                          }}
                        > 
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table></div>
        );

      case "checkbox":
        return (
          <div className="d-flex flex-column gap-2">
            {question.options.map((option, idx) => (
              <Button
                key={idx}
                variant={answer && answer.includes(option) ? "subtle" : "light-solid"}
                className="p-3 d-flex align-items-center gap-2"
                disabled
              >
                <Form.Check
                  type="checkbox"
                  checked={answer && answer.includes(option)}
                  disabled
                  onChange={() => {}}
                  style={{ pointerEvents: "none" }}
                />
                {option}
              </Button>
            ))}
          </div>
        );

      case "radio":
        return (
          <div className="d-flex flex-column gap-2">
            {question.options.map((option, idx) => (
              <Button
                key={idx}
                variant={answer === option ? "subtle" : "light-solid"}
                className="p-3 d-flex align-items-center gap-2"
                disabled
              >
                <Form.Check
                  type="radio"
                  label={<span>{option}</span>}
                  checked={answer === option}
                  disabled
                  className="mb-0"
                />
              </Button>
            ))}
          </div>
        );

      case "dropdown":
        return (
          <Form.Select disabled value={answer || ""}>
            <option value="">Select an option</option>
            {question.options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        );

      case "rating":
        return (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {getRatingOptions(question).map((opt) => (
              <Button
                key={String(opt.value)}
                variant={String(answer) === String(opt.value) ? "subtle" : "light-solid"}
                className="p-3 d-flex align-items-center gap-2 w-100"
                disabled
              >
                <Form.Check
                  type="radio"
                  checked={String(answer) === String(opt.value)}
                  disabled
                  onChange={() => {}}
                  style={{ pointerEvents: "none" }}
                  className="m-0"
                />
                <span>{opt.label}</span>
              </Button>
            ))}
          </div>
        );

      default:
        return (
          <div className="p-3 rounded-3 bg-light">
            {JSON.stringify(answer)}
          </div>
        );
    }
  };

  return (
    <div className="min-vh-100">
      <PageTitle
        title={evaluation.template?.title || "Completed Evaluation"}
        description="View completed evaluation details and responses"
      />
      <Navbar pageTitle={evaluation.template?.title || "Completed Evaluation"} />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <div className="d-flex flex-md-row align-items-md-center align-items-start flex-column justify-content-md-between justify-content-start gap-2">
            <div className="d-flex flex-row align-items-center justify-content-start gap-2">
              <h5 className="fw-semibold mb-0 py-md-3 py-2">
                {evaluation.title || evaluation.template?.title}
              </h5>  
              <span className={`badge small ${
                evaluation.template?.evaluationType === 'performance' ? 'bg-primary' : 
                evaluation.template?.evaluationType === 'feedback' ? 'bg-info' :
                evaluation.template?.evaluationType === 'survey' ? 'bg-success' :
                evaluation.template?.evaluationType === 'questionnaire' ? 'bg-warning' :
                evaluation.template?.type === 'performance' ? 'bg-primary' : 
                evaluation.template?.type === 'feedback' ? 'bg-info' :
                evaluation.template?.type === 'survey' ? 'bg-success' :
                evaluation.template?.type === 'questionnaire' ? 'bg-warning' :
                'bg-secondary'
              }`}>
                {evaluation.template?.evaluationType || evaluation.template?.type || "Evaluation"}
              </span>
            </div>
            <div className="d-flex py-md-0 py-2 flex-row align-items-center justify-content-end gap-3">
              <Button
                variant="light-solid"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="d-flex align-items-center gap-2"
              >
                <span className="d-none d-md-inline">{showDetails ? 'Hide' : 'Show'} Details</span>
                <i className={`bi ${showDetails ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
          {showDetails && (
            <div className="border-top pt-3 pb-3">
              <Row className="g-3">
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Evaluator:</span>
                    <div className="text-muted">
                      {evaluation.evaluation?.evaluator && evaluation.evaluation.evaluator.firstName ? (
                        <>
                          {evaluation.evaluation.evaluator.firstName} {evaluation.evaluation.evaluator.lastName}
                          {evaluation.evaluation.evaluator.role && (
                            <Badge bg="secondary" className="ms-2">
                              {evaluation.evaluation.evaluator.role}
                            </Badge>
                          )}
                        </>
                      ) : evaluation.submittedBy && evaluation.submittedBy.firstName ? (
                        <>
                          {evaluation.submittedBy.firstName} {evaluation.submittedBy.lastName}
                          {evaluation.submittedBy.role && (
                            <Badge bg="secondary" className="ms-2">
                              {evaluation.submittedBy.role}
                            </Badge>
                          )}
                        </>
                      ) : evaluation.evaluator && evaluation.evaluator.firstName ? (
                        <>
                          {evaluation.evaluator.firstName} {evaluation.evaluator.lastName}
                          {evaluation.evaluator.role && (
                            <Badge bg="secondary" className="ms-2">
                              {evaluation.evaluator.role}
                            </Badge>
                          )}
                        </>
                      ) : evaluation.template?.facultyName ? (
                        <div>{evaluation.template.facultyName}</div>
                      ) : (
                        <span className="text-muted">Not available</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Due Date:</span>
                    <div> <p className="mb-0 text-muted">{evaluation.dueDate ? new Date(evaluation.dueDate).toLocaleDateString() : evaluation.template?.endTime ? new Date(evaluation.template.endTime).toLocaleDateString() : "Not specified"}</p></div>
                  </div>
                </Col>                    
                {evaluation.updatedAt && evaluation.updatedAt !== evaluation.createdAt && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Last Updated:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(evaluation.updatedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                {evaluation.template?.startTime && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Start Date:</span>
                      <div><p className="mb-0 text-muted">{new Date(evaluation.template.startTime).toLocaleDateString()}</p></div>
                    </div>
                  </Col>
                )}
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Submitted On:</span>
                    <div>
                      <p className="mb-0 text-muted">{evaluation.createdAt ? new Date(evaluation.createdAt).toLocaleString() : formattedDate}</p>
                    </div>
                  </div>
                </Col>
                {evaluation.resident && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Resident:</span>
                      <div>
                        <p className="mb-0 text-muted">{evaluation.resident.firstName} {evaluation.resident.lastName}</p>
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Total Questions:</span>
                    <div><p className="mb-0 text-muted">{totalQuestions}</p></div>
                  </div>
                </Col>
                {evaluation.template?.isTimed && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Timed:</span>
                      <div>
                        <p className="mb-0 text-muted">{evaluation.template.timeLimit} minutes</p>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Container>
      </div>
      <Container className="py-4">
        <Row>
          <Col lg={3} md={4} className="d-none d-md-block">
            <div className="position-sticky" style={{ top: 80 }}>
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="border-0 px-4 pt-3 fw-bold mb-2">
                  Progress
                </Card.Header>
                <Card.Body className="px-4 py-0">
                  <div className="mb-3">
                
                    <ProgressBar
                      now={progressPercent}
                      variant={
                        progressPercent === 100 ? "success-solid" : "primary-solid"
                      }
                      style={{ height: 6 }}
                      animated
                    />   
                    <div className="d-flex flex-row align-items-center justify-content-between mt-2">
                    <span className="small gap-2">
                        Completion <strong className="fw-semibold">{progressPercent}%</strong>
                    </span>
                    <span className="text-muted small">
                      {answeredCount}/{totalQuestions} answered
                    </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm rounded-4">
                <Card.Header className="border-0  px-4 pt-3 pb-2 fw-bold mb-2">
                  Questions
                </Card.Header>
                <Card.Body className="px-3 pt-0">
                  <ul className="list-unstyled mb-0">
                    {evaluation?.template?.introHtml && evaluation.template.introHtml.trim() && (
                      <li key="intro" className="mb-2">
                        <Button
                          variant="light"
                          className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                          style={{ overflow: "hidden" }}
                          onClick={() => {
                            const node = questionRefs.current["intro"];
                            if (node) {
                              node.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 text-start">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center border text-muted"
                              style={{ width: 32, height: 32 }}
                            >
                              <i className="bi bi-info-lg text-muted" />
                            </div>
                            <div>
                              <div
                                className="fw-semibold text-truncate small"
                                style={{ maxWidth: 180 }}
                              >
                                Introduction
                              </div>
                            </div>
                          </div>
                        </Button>
                      </li>
                    )}
                    {numberedQuestions.map((question, idx) => {
                      const answered = isQuestionFilled(question);
                      const qId = question.id || `question-${idx}`;
                      return (
                        <li key={qId} className="mb-2">
                          <Button
                            variant="light"
                            className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                            style={{ overflow: "hidden" }}
                            onClick={() => {
                              const node = questionRefs.current[qId];
                              if (node) {
                                node.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 text-start">
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                  answered
                                    ? "bg-success text-white"
                                    : "border text-muted"
                                }`}
                                style={{ width: 32, height: 32 }}
                              >
                                {answered ? (
                                  <i className="bi bi-check" />
                                ) : (
                                  <span className="fw-semibold">{idx + 1}</span>
                                )}
                              </div>
                              <div>
                                <div
                                  className="fw-semibold text-truncate small"
                                  style={{ maxWidth: 180 }}
                                >
                                  {question.questionText || `Question ${idx + 1}`}
                                </div>
                                {/*<div className=" small" sty>
                                  {answered ? "Answered" : "Not answered"}
                                </div>*/}
                              </div>
                            </div>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>
          <Col lg={9} md={8}>
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body className="p-0">
                {evaluation?.template?.introHtml && (
                  <div
                    className="mb-4 mx-4 mt-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                    style={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: evaluation.template.introHtml }}
                    ref={(el) => {
                      if (el) {
                        questionRefs.current["intro"] = el;
                      }
                    }}
                  />
                )}
                {questions.map((question, index) => {
                  // Count only numbered questions (excluding header-paragraph and divider) up to current index
                  const questionNumber = questions
                    .slice(0, index + 1)
                    .filter(
                      (q) =>
                        !["header-paragraph", "divider"].includes(q.questionType)
                    ).length;

                  return (
                    <div
                      key={`question-${question.id}-${index}`}
                      className="mb-5 pb-5 mt-4 px-4 eval-question-border"
                      ref={(el) => {
                        const key = question.id || `question-${index}`;
                        if (el) {
                          questionRefs.current[key] = el;
                        }
                      }}
                    >
                      <div>
                        {["header-paragraph", "divider"].includes(
                          question.questionType
                        ) ? (
                          question.questionType === "divider" ? (
                            <hr
                              style={{
                                borderTop: `2px ${question.style || "solid"} #ccc`,
                                margin: "32px 0",
                              }}
                            />
                          ) : (
                            renderAnswer(question)
                          )
                        ) : (
                          <>
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <span
                                className=" bg-light fw-semibold px-3 py-2 rounded-3"
                              >
                                Question {questionNumber}
                              </span>
                              <div>
                                <h5 className="fw-semibold mb-0">
                                  {question.questionText ||
                                    `Question ${questionNumber}`}
                                </h5>
                                {/*<div className="text-muted small text-capitalize">
                                  {question.questionType?.replace(/-/g, " ")}
                                </div>*/}
                              </div>
                            </div>
                            {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "before" && (
                              <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                            )}
                            {renderAnswer(
                              question,
                              evaluation.answers[question.id]
                            )}
                            {evaluation.comments &&
                              evaluation.comments[question.id] && (
                                <div className="mt-3 p-3 bg-light rounded">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bi bi-chat-dots text-info me-2"></i>
                                    <strong className="text-info">
                                      Comment:
                                    </strong>
                                  </div>
                                  <p className="mb-0">
                                    {evaluation.comments[question.id]}
                                  </p>
                                </div>
                              )}
                            {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "after" && (
                              <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
              <Card.Footer
                className="d-flex justify-content-end py-3 px-4"
                style={{
                  borderTop: "1px dashed var(--border-bg)",
                  backgroundColor: "var(--bs-card-bg)",
                }}
              >
                <Button onClick={() => navigate(-1)} variant="primary">
                  Back
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CompletedEvaluationView;
